/**
 * dashboard/classes/$classId.progress.tsx — Fase 5 N-14 tutor class progress.
 *
 * Lives at `/dashboard/classes/$classId/progress`. Auth-gated to approved
 * tutors; reuses the same `requireTutor` pre-flight as the class detail
 * route. The actual progress surface is rendered by `<ClassProgressDashboard>`.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ClassProgressDashboard } from '../../../components/tutor/ClassProgressDashboard.js';
import { freshSessionQuery, classDetailQuery, type SessionPrincipal } from '../../../lib/api/queries.js';
import { PageHeader } from '../../../components/ui/PageHeader.js';

export const Route = createFileRoute('/dashboard/classes/$classId/progress')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: ClassProgressRoute,
});

function ClassProgressRoute() {
  const { classId } = Route.useParams();
  // Resolve the class name for the heading (BUG-UX-01) so tutors see
  // "Progres kelas Kucing & Kode" instead of a raw UUID. Uses the
  // existing cached detail query; falls back to the id while loading.
  const classDetail = useQuery(classDetailQuery(classId));
  const rawName = classDetail.data?.ok ? (classDetail.data.data.name ?? classId) : classId;
  const title = rawName.startsWith('Progres kelas ') ? rawName : `Progres kelas ${rawName}`;
  return (
    <main className="page-shell" data-testid="class-progress-page">
      <PageHeader
        eyebrow="Progres kelas"
        title={title}
        description="Lihat ringkasan progres siswa, daftar per-siswa, dan aktivitas terbaru di kelas ini."
      />
      <ClassProgressDashboard classId={classId} />
    </main>
  );
}